import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextButton,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  classes,
  styled,
} from 'bonfire-kindling';
import _keyBy from 'lodash/keyBy';
import _mapValues from 'lodash/mapValues';
import Belt from 'components/Belt/Belt';
import Item from 'components/Item/Item';
import WelcomeBanner from 'components/WelcomeBanner/WelcomeBanner';
import { regionsGrouped, countries } from 'constants/regions';
import { useTranslation } from 'react-i18next';

const StyledFormControlLabel = styled(FormControlLabel)(() => ({
  [`&.${classes.components.MuiFormControlLabel.root}`]: {
    [`&.${classes.states.MuiFocusVisible}`]: {
      backgroundColor: 'rgba(42, 109, 182, 0.22)',
    },
  },
  [`&.${classes.components.MuiFormControlLabel.label}`]: {
    fontSize: '0.75rem',
  },
}));

const RegionPicker = ({
  countryName,
  regionCode,
  selectedRegions,
  allSubregions,
  searchedSubregions,
  isExpanded,
  isSearching,
  expandCountry,
  addCountry,
  removeCountry,
  addRegion,
  removeRegion,
  regionCheckboxDisabled,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isSearching && searchedSubregions.length && !isExpanded) {
      expandCountry();
    }
  }, [searchedSubregions.length, isSearching, isExpanded, expandCountry]);

  // No results were found for this region while searching.
  if (isSearching && !searchedSubregions.length) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Item
        onExpandClick={() => expandCountry()}
        onChange={e => (e.target.checked ? addCountry() : removeCountry())}
        isSelected={selectedRegions.length === allSubregions.length}
        indeterminate={allSubregions.length > selectedRegions.length && selectedRegions.length > 0}
        checkboxDisabled={regionCheckboxDisabled}
        isExpanded={isExpanded}
        isExpandable
        cypress={`${regionCode}Checkbox`}
        addLabel={t('settings.add_label', { title: countryName })}
        removeLabel={t('settings.remove_label', { title: countryName })}
        variant="loose"
      >
        <Box pl={1}>
          <Typography variant="body2">{countryName}</Typography>
        </Box>
      </Item>
      {isExpanded &&
        searchedSubregions.map(subregion => (
          <Item
            isSelected={selectedRegions.includes(subregion.value)}
            onChange={e =>
              e.target.checked ? addRegion(subregion.value) : removeRegion(subregion.value)
            }
            addLabel={t('settings.add_label', { title: subregion.label })}
            removeLabel={t('settings.remove_label', { title: subregion.label })}
            sx={{ marginLeft: theme => theme.spacing(3) }}
            key={subregion.value}
            variant="loose"
          >
            <Box pl={4}>
              <Typography variant="body2">{subregion.label}</Typography>
            </Box>
          </Item>
        ))}
    </Box>
  );
};

const stripCaseInsensitiveString = str => str.toLowerCase().replace(/\s+/g, '');
const searchRegion = (search, subregions) =>
  subregions.filter(subregion => stripCaseInsensitiveString(subregion.label).includes(search));
const noop = () => {};
const countriesIndexed = _keyBy(countries, 'value');
const emptyCountryGroups = _mapValues(regionsGrouped, () => []);
const fullCountryGroups = _mapValues(regionsGrouped, regions => regions.map(({ value }) => value));
const numRegions = Object.values(fullCountryGroups).flat().length;

const LocationPicker = ({
  initialValues,
  formFooter,
  onSubmit: _onSubmit,
  onChange = noop,
  isOnboarding = false,
}) => {
  const { t } = useTranslation();
  const [allLocationsChecked, setAllLocationsChecked] = useState(false);
  const [isExpanded, setIsExpanded] = useState({});

  const [selectedLocationsGrouped, setSelectedLocationsGrouped] = useState(
    initialValues.reduce(
      (grouped, location) => {
        const [countryCode] = location.split('.');
        if (grouped[countryCode]) {
          grouped[countryCode].push(location);
        } else {
          grouped.other.push(location);
        }
        return grouped;
      },
      // Avoid mutating emptyCountryGroups above; could deep copy instead
      _mapValues(regionsGrouped, () => [])
    )
  );
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const flatRegions = Object.values(selectedLocationsGrouped).flat();
    onChange(flatRegions);

    const isAnyLocationUnchecked = flatRegions.length !== numRegions;

    if (isAnyLocationUnchecked) {
      setAllLocationsChecked(false);
    } else {
      setAllLocationsChecked(true);
    }
  }, [selectedLocationsGrouped, onChange]);

  const onAllLocationsChanged = ({ target: { checked } }) => {
    if (checked) {
      setSelectedLocationsGrouped(fullCountryGroups);
      setAllLocationsChecked(true);
    } else {
      setSelectedLocationsGrouped(emptyCountryGroups);
      setAllLocationsChecked(false);
    }
  };

  const onSubmit = async e => {
    e.preventDefault();
    setIsSaving(true);

    await _onSubmit(Object.values(selectedLocationsGrouped).flat());
    setIsSaving(false);
  };

  const searchedRegionsByRegion = useMemo(() => {
    const results = { ...emptyCountryGroups };
    const search = stripCaseInsensitiveString(searchQuery);

    // If the user is strictly searching for the region itself, then
    // return all subregions.
    if (!searchQuery) {
      return results;
    } else if (stripCaseInsensitiveString('Canada') === search) {
      results['CA'] = [...regionsGrouped['CA']];
    } else if (stripCaseInsensitiveString('United States') === search) {
      results['US'] = [...regionsGrouped['US']];
    }

    Object.keys(regionsGrouped).forEach(groupKey => {
      if (!results[groupKey].length) {
        results[groupKey] = searchRegion(search, regionsGrouped[groupKey]);
      }
    });

    return results;
  }, [searchQuery]);

  const isSearchingWithNoResults =
    !!searchQuery && Object.values(searchedRegionsByRegion).every(regions => !regions.length);

  return (
    <Box mt={3}>
      <WelcomeBanner
        bannerBody={
          isOnboarding
            ? t('settings.location.select_instructions')
            : `${t('settings.location.select_instructions')} ${t(
                'settings.location.select_warning'
              )}`
        }
      />
      <TextField
        name="search"
        type="search"
        placeholder={t('settings.location.search_placeholder')}
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        sx={{
          width: '100%',
          my: 2,
          flex: 2,
          maxWidth: { xs: 'initial', md: isOnboarding ? 'initial' : '400px' },
          alignSelf: 'stretch',
        }}
      />
      {isSearchingWithNoResults ? (
        <Typography variant="body2" component="p">
          {t('settings.location.search_no_results')}
        </Typography>
      ) : (
        <form onSubmit={onSubmit}>
          {!searchQuery && (
            <Box sx={{ display: 'flex' }}>
              <StyledFormControlLabel
                control={<Checkbox color="primary" />}
                data-cy="allLocations"
                name="allLocations"
                checked={allLocationsChecked}
                onChange={onAllLocationsChanged}
                label={t('settings.location.select_all')}
              />
            </Box>
          )}
          <Box mb={4}>
            <Belt
              sx={{
                alignItems: 'flex-start',
                flexDirection: isOnboarding ? 'column' : { xs: 'column', md: 'row' },
                '& > * + *': {
                  ml: isOnboarding ? 0 : { xs: 0, md: 4 },
                  mt: isOnboarding ? 1 : { xs: 1, md: 0 },
                },
              }}
            >
              {Object.entries(regionsGrouped).map(([key, subregions]) => (
                <RegionPicker
                  key={key}
                  countryName={
                    countriesIndexed[key]?.label ?? t('opportunities.filters.other_regions')
                  }
                  regionCode={key}
                  selectedRegions={selectedLocationsGrouped[key]}
                  allSubregions={subregions}
                  searchedSubregions={searchQuery ? searchedRegionsByRegion[key] : subregions}
                  regionCheckboxDisabled={
                    !!searchQuery && searchedRegionsByRegion[key].length !== subregions.length
                  }
                  isExpanded={isExpanded[key] || false}
                  isSearching={!!searchQuery}
                  expandCountry={() => setIsExpanded(prev => ({ ...prev, [key]: !prev[key] }))}
                  addCountry={() =>
                    setSelectedLocationsGrouped(prev => ({
                      ...prev,
                      [key]: fullCountryGroups[key],
                    }))
                  }
                  removeCountry={() =>
                    setSelectedLocationsGrouped(prev => ({ ...prev, [key]: [] }))
                  }
                  addRegion={subregion =>
                    setSelectedLocationsGrouped(prev => ({
                      ...prev,
                      [key]: [...prev[key], subregion],
                    }))
                  }
                  removeRegion={subregionToRemove =>
                    setSelectedLocationsGrouped(prev => ({
                      ...prev,
                      [key]: prev[key].filter(subregion => subregion !== subregionToRemove),
                    }))
                  }
                />
              ))}
            </Belt>
          </Box>
          {formFooter ? (
            formFooter
          ) : (
            <TextButton
              type="submit"
              variant="contained"
              color="primary"
              isBusy={isSaving}
              sx={{ marginTop: '10px' }}
              label={t('settings.save')}
            />
          )}
        </form>
      )}
    </Box>
  );
};

export default LocationPicker;
